import React, { useEffect, useState } from 'react';
import useRingtone from './useRingtone';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Avatar, Box
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import CallEndIcon from '@mui/icons-material/CallEnd';
import { useSocket } from '../../context/SocketContext';
import { getPendingCallForMe, declineCall } from '../../services/callsApi';

/**
 * Globally rendered modal that listens for server:video:incoming_call events
 * on the main Socket.IO connection. Shown to clients when their therapist
 * starts a call.
 *
 * On every (re)connect it also polls GET /api/calls/pending-for-me so that
 * a push event missed during a socket reconnect / tab throttle is recovered.
 */
export default function IncomingCallModal({ currentUser, token }) {
  const socket = useSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const [pending, setPending] = useState(null);

  useEffect(() => {
    if (!socket) return;

    const roles = currentUser?.role;
    const isClient = Array.isArray(roles) ? roles.includes('client') : roles === 'client';
    if (!isClient) return;

    // The waiting room connects the call itself (ST-20260915-0005). Remembering
    // a call pushed while the client waits there made the ring box pop up over
    // the call screen they had just been taken to.
    const onWaitingRoom = () => /^\/appointments\/[^/]+\/waiting/.test(window.location.pathname);

    const onIncoming = (payload) => {
      if (onWaitingRoom()) return;
      setPending(prev => prev || payload);
    };
    socket.on('server:video:incoming_call', onIncoming);

    // Recovery polling — if a push event was missed during a reconnect gap,
    // tab-throttling, or while the user was on a stale call page, this
    // surfaces the call retroactively. Triggers:
    //   1. Initial mount.
    //   2. Every socket `connect` event (covers in-session reconnects).
    //   3. Tab visibility change → visible (covers backgrounded-tab case).
    //   4. A 30s timer (safety net for any other edge case).
    let cancelled = false;
    const recoveryPoll = async () => {
      if (cancelled || !token) return;
      try {
        const { call } = await getPendingCallForMe({ token });
        if (cancelled || !call || onWaitingRoom()) return;
        setPending(prev => prev || call);
      } catch (err) {
        if (err?.response?.status && err.response.status !== 401) {
          console.warn('[IncomingCallModal] pending-for-me poll failed:', err.message);
        }
      }
    };
    recoveryPoll();
    socket.on('connect', recoveryPoll);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') recoveryPoll();
    };
    document.addEventListener('visibilitychange', onVisibility);
    const intervalId = setInterval(recoveryPoll, 30 * 1000);

    return () => {
      cancelled = true;
      socket.off('server:video:incoming_call', onIncoming);
      socket.off('connect', recoveryPoll);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(intervalId);
    };
  }, [socket, currentUser, token]);

  const handleAccept = () => {
    const id = pending?.liveCallId;
    setPending(null);
    if (id) navigate(`/call/${id}`);
  };

  const handleDecline = () => {
    const id = pending?.liveCallId;
    setPending(null);
    // Tell the server, which ends the call and tells the therapist. Before this
    // declining only closed the box (ST-20260911-0002/0003).
    if (id) {
      declineCall({ token, liveCallId: id }).catch(err =>
        console.warn('[IncomingCallModal] decline failed:', err?.message));
    }
  };

  // The box closes only from its buttons: no onClose, and Escape is disabled.
  // A click outside used to decline the call silently (ST-20260911-0001).

  // The waiting room connects the call itself, without ringing (ST-20260915-0005).
  const hidden = !pending
    || /^\/appointments\/[^/]+\/waiting/.test(location.pathname)
    // Already on this call's screen: nothing to answer.
    || location.pathname === `/call/${pending.liveCallId}`;

  // Rings for exactly as long as the box is showing (ST-20260923-0005).
  useRingtone(!hidden);

  if (hidden) return null;

  return (
    <Dialog open maxWidth="xs" fullWidth disableEscapeKeyDown>
      <DialogTitle>Incoming video call</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <VideocamIcon />
          </Avatar>
          <Box>
            <Typography variant="subtitle1">
              {pending.therapistName || 'Your therapist'} is calling
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Tap Accept to join
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleDecline}
          startIcon={<CallEndIcon />}
          color="error"
        >
          Decline
        </Button>
        <Button
          onClick={handleAccept}
          startIcon={<VideocamIcon />}
          variant="contained"
        >
          Accept
        </Button>
      </DialogActions>
    </Dialog>
  );
}
